import axiosInstance from "../api/api";
import axios from "axios"; // Using standard axios for direct upload requests to avoid our interceptors

export class MediaUploadService {
  /**
   * Upload a single media file to storage (Cloudinary or S3), then save metadata to backend.
   *
   * @param {File} file - The file to upload.
   * @param {Object} options - { folder: string, tags: string[] }
   * @param {Function} onProgress - Callback(progressPercent)
   * @returns {Object} - The saved Media object from the backend (contains _id, secure_url, etc.)
   */
  static async uploadMedia(file, options = { folder: "general", tags: [] }, onProgress) {
    // 1. Get Signature or presigned URL(s) from backend
    const { data: signData } = await axiosInstance.post("/media/sign", {
      folder: options.folder,
      tags: options.tags,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });

    console.log(
      `[MediaUploadService] Presigned upload data received | Provider: ${signData.provider} | Key/Folder: ${signData.key || signData.folder}`
    );

    // 2. Route based on provider returned by backend
    if (signData.provider === "s3") {
      return this._uploadS3(file, signData, options.folder, onProgress);
    }

    // Cloudinary path
    const { signature, timestamp, api_key, cloud_name, folder, tags } = signData;
    const isChunked = file.size >= 5 * 1024 * 1024;
    let cloudinaryResponse;

    if (isChunked) {
      cloudinaryResponse = await this._uploadChunked(file, {
        signature,
        timestamp,
        api_key,
        cloud_name,
        folder,
        tags,
      }, onProgress);
    } else {
      cloudinaryResponse = await this._uploadNormal(file, {
        signature,
        timestamp,
        api_key,
        cloud_name,
        folder,
        tags,
      }, onProgress);
    }

    // Save Metadata to Backend
    const { data: mediaData } = await axiosInstance.post("/media", {
      secure_url: cloudinaryResponse.secure_url,
      public_id: cloudinaryResponse.public_id,
      asset_id: cloudinaryResponse.asset_id,
      resource_type: cloudinaryResponse.resource_type,
      bytes: cloudinaryResponse.bytes,
      width: cloudinaryResponse.width,
      height: cloudinaryResponse.height,
      duration: cloudinaryResponse.duration,
      format: cloudinaryResponse.format,
      tags: cloudinaryResponse.tags,
      folder: folder,
      provider: "cloudinary",
    });

    return mediaData.media;
  }

  /**
   * S3 upload strategy (Single presigned PUT or Multipart Chunked)
   */
  static async _uploadS3(file, signData, folder, onProgress) {
    const resourceType = file.type.startsWith("video") ? "video" : file.type.startsWith("image") ? "image" : "raw";

    console.log(
      `[MediaUploadService] Starting S3 upload | File: ${file.name} (${file.size} bytes) | Key: ${signData.key} | Chunked: ${signData.isChunked}`
    );

    if (!signData.isChunked) {
      await axios.put(signData.uploadUrl, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
      });

      console.log(`[MediaUploadService] S3 direct PUT completed | Key: ${signData.key}`);
    } else {
      const completedParts = [];
      const totalParts = signData.partUrls.length;

      for (const part of signData.partUrls) {
        const start = (part.partNumber - 1) * signData.chunkSize;
        const end = Math.min(start + signData.chunkSize, file.size);
        const chunk = file.slice(start, end);

        console.log(
          `[MediaUploadService] Uploading S3 Part ${part.partNumber}/${totalParts} (${chunk.size} bytes)`
        );

        const resp = await axios.put(part.url, chunk, {
          headers: { "Content-Type": file.type },
        });

        const etag = resp.headers.etag || resp.headers.ETag;
        console.log(
          `[MediaUploadService] S3 Part ${part.partNumber}/${totalParts} uploaded | ETag: ${etag}`
        );

        completedParts.push({ PartNumber: part.partNumber, ETag: etag });

        if (onProgress) {
          onProgress(Math.round((end * 100) / file.size));
        }
      }

      console.log(
        `[MediaUploadService] Completing S3 multipart upload via backend | Key: ${signData.key} | UploadId: ${signData.uploadId}`
      );

      await axiosInstance.post("/media/complete-multipart", {
        key: signData.key,
        uploadId: signData.uploadId,
        parts: completedParts,
      });

      console.log(`[MediaUploadService] S3 multipart upload completed | Key: ${signData.key}`);
    }

    console.log(`[MediaUploadService] Saving S3 media metadata to backend | Key: ${signData.key}`);

    const { data: mediaData } = await axiosInstance.post("/media", {
      secure_url: signData.key,
      public_id: signData.key,
      resource_type: resourceType,
      bytes: file.size,
      folder: folder || "general",
      provider: "s3",
    });

    console.log(
      `[MediaUploadService] S3 media record created in DB | ID: ${mediaData.media._id} | Key: ${signData.key}`
    );

    // Wait for Lambda to process the raw file into images/full/<uuid>.webp.
    // Without this, the component would get a 403 immediately after save because
    // Lambda hasn't finished converting yet. We probe with Image() (no CORS needed).
    const processedUrl = mediaData.media?.secure_url || mediaData.media?.url;
    if (resourceType === "image" && processedUrl && processedUrl.includes("/images/")) {
      console.log(`[MediaUploadService] Waiting for Lambda to process image: ${processedUrl}`);
      await MediaUploadService._waitForLambda(processedUrl);
    }

    return mediaData.media;
  }

  /**
   * Polls a CloudFront URL using the browser Image API until the file returns 200.
   * Handles the async Lambda processing delay after S3 upload.
   * Uses new Image() instead of fetch() to avoid CORS preflight requirements.
   *
   * @param {string} url - The processed CloudFront URL (e.g. .../images/full/<uuid>.webp)
   * @param {number} maxWaitMs - Max total wait in ms before giving up (default 12000)
   */
  static _waitForLambda(url, maxWaitMs = 12000) {
    return new Promise((resolve) => {
      const intervalMs = 2000;
      const maxAttempts = Math.ceil(maxWaitMs / intervalMs);
      let attempts = 0;

      const probe = () => {
        const img = new Image();
        img.onload = () => {
          console.log(
            `[MediaUploadService] Lambda processed image ready after ~${(attempts * intervalMs) + 1500}ms`
          );
          resolve();
        };
        img.onerror = () => {
          attempts++;
          if (attempts < maxAttempts) {
            console.log(
              `[MediaUploadService] Lambda not ready yet, retrying in ${intervalMs}ms (${attempts}/${maxAttempts})`
            );
            setTimeout(probe, intervalMs);
          } else {
            console.warn(
              `[MediaUploadService] Lambda processing timeout after ${maxWaitMs}ms — returning anyway`
            );
            resolve(); // graceful degradation: proceed even if Lambda is slow
          }
        };
        // Cache-bust param prevents browser serving a cached 403 response
        img.src = `${url}?_t=${Date.now()}`;
      };

      // Initial delay: Lambda needs a moment to trigger from the S3 event
      setTimeout(probe, 1500);
    });
  }

  /**
   * Standard Cloudinary upload
   */
  static async _uploadNormal(file, auth, onProgress) {
    const formData = new FormData();
    formData.append("api_key", auth.api_key);
    formData.append("timestamp", auth.timestamp);
    formData.append("signature", auth.signature);
    if (auth.folder) formData.append("folder", auth.folder);
    if (auth.tags) formData.append("tags", auth.tags.join(","));
    formData.append("file", file);

    const resourceType = file.type.startsWith("video") ? "video" : file.type.startsWith("image") ? "image" : "raw";
    const url = `https://api.cloudinary.com/v1_1/${auth.cloud_name}/${resourceType}/upload`;

    const response = await axios.post(url, formData, {
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });

    return response.data;
  }

  /**
   * Chunked Cloudinary upload
   */
  static async _uploadChunked(file, auth, onProgress) {
    const chunkSize = 5 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);
    const uniqueUploadId = `chunked_upload_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    const resourceType = file.type.startsWith("video") ? "video" : file.type.startsWith("image") ? "image" : "raw";
    const url = `https://api.cloudinary.com/v1_1/${auth.cloud_name}/${resourceType}/upload`;

    let cloudinaryResponse = null;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append("api_key", auth.api_key);
      formData.append("timestamp", auth.timestamp);
      formData.append("signature", auth.signature);
      if (auth.folder) formData.append("folder", auth.folder);
      if (auth.tags) formData.append("tags", auth.tags.join(","));
      formData.append("file", chunk, file.name);

      const headers = {
        "X-Unique-Upload-Id": uniqueUploadId,
        "Content-Range": `bytes ${start}-${end - 1}/${file.size}`,
      };

      try {
        const response = await axios.post(url, formData, { headers });
        cloudinaryResponse = response.data;

        if (onProgress) {
          const percentCompleted = Math.round((end * 100) / file.size);
          onProgress(percentCompleted);
        }
      } catch (error) {
        console.error(`Chunk upload failed at chunk ${i + 1}/${totalChunks}`, error?.response?.data || error);
        throw error;
      }
    }

    return cloudinaryResponse;
  }

  /**
   * Helper to upload multiple files with progress tracking
   */
  static async uploadMultiple(files, options, onProgressObj) {
    const results = [];
    for (let i = 0; i < files.length; i++) {
      const media = await this.uploadMedia(files[i], options, (progress) => {
        if (onProgressObj) onProgressObj(i, progress);
      });
      results.push(media);
    }
    return results;
  }

  /**
   * Delete a media item from the backend (and Cloudinary / S3).
   * @param {string} mediaId - The backend Media._id
   */
  static async deleteMedia(mediaId) {
    if (!mediaId) return;
    try {
      console.log(`[MediaUploadService] Deleting media item ID: ${mediaId}`);
      await axiosInstance.delete(`/media/${mediaId}`);
      console.log(`[MediaUploadService] Media item deleted | ID: ${mediaId}`);
    } catch (err) {
      console.warn("deleteMedia failed (non-fatal):", err?.message);
    }
  }
}
